import GeoAPI from 'api/geo.api';
import { updateLoadingState } from 'store/app.slice';
import {
  type AssetTypeGeoTripData,
  type GeoTripAnalysis,
} from 'types/geo.types';

import { BaseHandler } from './base.handler';
import {
  setGeoLoading,
  setLatestTripsForEachFleetAsset,
  setDynamicPits,
} from 'store/geo.slice';
import { type ASSET_TYPES_ENUM } from 'types/asset.types';

export default class GeoHandler extends BaseHandler {
  private readonly api: GeoAPI;

  constructor() {
    super();

    this.api = new GeoAPI();
  }

  async get(): Promise<any> {
    try {
      const cacheKey = `geo_cache_fleet`;
      const cachedData = sessionStorage.getItem(cacheKey);

      if (cachedData) {
        const data = JSON.parse(cachedData);

        // Check if the cache is stale (older than 24 hours)
        const cacheTimestamp = new Date(data.timestamp);
        const currentTime = new Date();
        const hoursDifference =
          (currentTime.getTime() - cacheTimestamp.getTime()) / (1000 * 60 * 60);

        if (hoursDifference < 24) {
          this.dispatch(setLatestTripsForEachFleetAsset(data));

          return data;
        }
      }
      this.dispatch(setGeoLoading(true));
      // Fetch fresh data if cache is empty or stale
      const freshData = await this.api.getLatestTripsByFleetAssets(); // Replace with your API call
      sessionStorage.setItem(
        cacheKey,
        JSON.stringify({ ...freshData, timestamp: new Date() })
      );

      // Dispatch the fresh data
      this.dispatch(setLatestTripsForEachFleetAsset(freshData));
      return freshData;
    } catch (_) {
      this.dispatch(setGeoLoading(false));
      this.handleError('An error occurred while fetching geo data.');
    }
  }

  async getTrips(
    deviceId: string,
    startDateTime: string,
    endDateTime: string
  ): Promise<GeoTripAnalysis> {
    try {
      const geoData = await this.api.getTrips(
        deviceId,
        startDateTime,
        endDateTime
      );

      return geoData;
    } catch (_) {
      this.handleError('An error occurred while fetching geo trips data.');
    }

    return {
      data: [],
      analysis: {},
    };
  }

  async getAssetTypeTrips(
    startDateTime: string,
    endDateTime: string,
    isShiftWise: boolean,
    bypassCache: boolean = false
  ): Promise<AssetTypeGeoTripData | undefined> {
    try {
      const cacheKey = `asset_type_trips_${startDateTime}_${endDateTime}`;

      if (!bypassCache) {
        const cachedData = sessionStorage.getItem(cacheKey);
        if (cachedData) {
          const data = JSON.parse(cachedData);
          return data.data;
        }
      }

      this.dispatch(setGeoLoading(true));
      const geoData = await this.api.getAssetTypeTrips(
        startDateTime,
        endDateTime,
        isShiftWise
      );

      sessionStorage.setItem(
        cacheKey,
        JSON.stringify({ ...geoData, timestamp: new Date() })
      );

      this.dispatch(setGeoLoading(false));
      return geoData.data;
    } catch (_) {
      this.dispatch(setGeoLoading(false));
      this.handleError(
        'An error occurred while fetching geo trips data by asset type.'
      );
      this.dispatch(updateLoadingState(false));
      return undefined;
    }
  }

  async getDynamicPits(
    customerCode: string,
    startDate?: string,
    endDate?: string
  ): Promise<any> {
    try {
      const cacheKey = `dynamic_geo_pits-${startDate}-${endDate}`;
      const cachedData = sessionStorage.getItem(cacheKey);

      if (cachedData) {
        const data = JSON.parse(cachedData);

        // Check if the cache is stale (older than 2 hours)
        const cacheTimestamp = new Date(data.timestamp);
        const currentTime = new Date();
        const hoursDifference =
          (currentTime.getTime() - cacheTimestamp.getTime()) / (1000 * 60 * 60);

        // Check if the cache is valid and the date parameters match
        if (
          hoursDifference < 2 &&
          data.startDate === startDate &&
          data.endDate === endDate
        ) {
          this.dispatch(setDynamicPits(data));

          return data;
        }
      }
      this.dispatch(setGeoLoading(true));
      const pits = await this.api.getDynamicPits(startDate, endDate);

      sessionStorage.setItem(
        cacheKey,
        JSON.stringify({
          ...pits,
          timestamp: new Date(),
          startDate,
          endDate,
        })
      );

      this.dispatch(setDynamicPits(pits));
      this.dispatch(setGeoLoading(false));
      return pits;
    } catch (_) {
      this.dispatch(setGeoLoading(false));
      this.handleError('An error occurred while fetching dynamic pits data.');
    }

    return {
      data: [],
      analysis: {},
    };
  }

  async getDeviceGpsData(
    startDate: string,
    endDate: string,
    deviceId: string,
    assetType: string
  ): Promise<any> {
    const gpsData = await this.api.getDeviceGpsDataPerTime(
      startDate,
      endDate,
      deviceId,
      assetType
    );
    return gpsData;
  }

  async getDeviceAllGpsData(
    dataPoints: Array<{
      device_id: string;
      start_ts: string;
      end_ts: string;
      asset_type: ASSET_TYPES_ENUM;
    }>
  ): Promise<any> {
    const gpsData = await this.api.getDeviceAllGpsDataPerTime(dataPoints);
    return gpsData;
  }

  async getGeoHashTrips(
    device: string,
    tripDate: string,
    isProductive: boolean
  ): Promise<any> {
    const geoHashTrips = await this.api.getGeoHashTrips(
      device,
      tripDate,
      isProductive
    );
    return geoHashTrips;
  }

  async addUpdateGeoHashTrips(data: any): Promise<any> {
    const geoHashTrips = await this.api.addUpdateGeoHashTrips(data);
    return geoHashTrips;
  }
}
